/*
 * PostHog event tracking for KPOPSEOULCITY storefront.
 *
 * Events:
 *   product_viewed     — PDP load
 *   collection_viewed  — collection page load
 *   add_to_cart        — successful /cart/add fetch
 *   cart_viewed        — cart drawer open OR /cart page load
 *   checkout_started   — checkout CTA click (cart page or drawer)
 *
 * Notes:
 *   - Relies on `window.posthog` from theme.liquid snippet.
 *   - Uses ShopifyAnalytics.meta for product/collection context (set by Shopify).
 *   - Intercepts fetch globally to catch /cart/add from any UI (PDP form, quick-add, etc).
 *   - Listens for the native HTMLDetailsElement 'toggle' on the cart drawer summary
 *     and a click delegate on checkout submits for the broadest compatibility.
 */
(function () {
  'use strict';

  function waitForPosthog(maxMs) {
    return new Promise(function (resolve) {
      var t0 = Date.now();
      (function tick() {
        if (window.posthog && typeof window.posthog.capture === 'function') return resolve(true);
        if (Date.now() - t0 > maxMs) return resolve(false);
        setTimeout(tick, 100);
      })();
    });
  }

  function safeCapture(event, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, props || {});
      }
    } catch (e) {
      // noop — never break the store
    }
  }

  function shopifyMeta() {
    try {
      return (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    } catch (e) {
      return {};
    }
  }

  function currency() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    } catch (e) {
      return 'USD';
    }
  }

  function pageType() {
    var m = shopifyMeta();
    return (m.page && m.page.pageType) || '';
  }

  // ----- product_viewed -----
  function trackProductViewed() {
    var m = shopifyMeta();
    var p = m.product;
    if (!p) return;
    var variant = (p.variants && p.variants[0]) || {};
    safeCapture('product_viewed', {
      product_id: p.id,
      variant_id: variant.id || null,
      title: p.title || (variant.public_title || ''),
      vendor: p.vendor || '',
      type: p.type || '',
      price: typeof variant.price === 'number' ? variant.price / 100 : null,
      currency: currency(),
      url: window.location.pathname,
    });
  }

  // ----- collection_viewed -----
  function trackCollectionViewed() {
    if (pageType() !== 'collection') return;
    safeCapture('collection_viewed', {
      handle: window.location.pathname.replace('/collections/', '').split('/')[0],
      url: window.location.pathname + window.location.search,
    });
  }

  // ----- cart_viewed -----
  function trackCartPage() {
    if (pageType() === 'cart' || window.location.pathname === '/cart') {
      safeCapture('cart_viewed', { source: 'page', url: window.location.pathname });
    }
  }

  function bindCartDrawerOpen() {
    // Dawn renders cart drawer inside <details id="Details-menu-..."> OR via custom element <cart-drawer>.
    // Simplest universal hook: listen for clicks on cart icon link.
    var cartIcons = document.querySelectorAll(
      'a.header__icon--cart, a[href$="/cart"], #cart-icon-bubble'
    );
    cartIcons.forEach(function (el) {
      el.addEventListener(
        'click',
        function () {
          safeCapture('cart_viewed', { source: 'drawer', url: window.location.pathname });
        },
        { passive: true }
      );
    });
  }

  // ----- add_to_cart -----
  function interceptAddToCart() {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    window.fetch = function (resource, init) {
      var url = '';
      try {
        url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      } catch (e) {}
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var promise = origFetch.apply(this, arguments);
      if (isAdd) {
        promise
          .then(function (resp) {
            if (!resp || !resp.ok) return;
            return resp.clone().json();
          })
          .then(function (data) {
            if (!data) return;
            // Shopify returns either a single item or {items: [...]}
            var items = Array.isArray(data.items) ? data.items : [data];
            items.forEach(function (item) {
              if (!item || !item.id) return;
              safeCapture('add_to_cart', {
                product_id: item.product_id,
                variant_id: item.variant_id || item.id,
                title: item.product_title || item.title || '',
                vendor: item.vendor || '',
                quantity: item.quantity || 1,
                price: typeof item.price === 'number' ? item.price / 100 : null,
                line_price: typeof item.line_price === 'number' ? item.line_price / 100 : null,
                currency: currency(),
                handle: item.handle || '',
              });
            });
          })
          .catch(function () {
            // swallow
          });
      }
      return promise;
    };
  }

  // ----- checkout_started -----
  function bindCheckoutClicks() {
    document.addEventListener(
      'click',
      function (e) {
        var target = e.target && e.target.closest
          ? e.target.closest('button[name="checkout"], a[href*="/checkouts/"], a[href="/checkout"]')
          : null;
        if (!target) return;
        // Best-effort cart total from /cart endpoint
        fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart) {
              safeCapture('checkout_started', {});
              return;
            }
            safeCapture('checkout_started', {
              item_count: cart.item_count,
              total_price: typeof cart.total_price === 'number' ? cart.total_price / 100 : null,
              currency: cart.currency || currency(),
              note: cart.note || '',
            });
          })
          .catch(function () {
            safeCapture('checkout_started', {});
          });
      },
      true
    );
  }

  function init() {
    trackProductViewed();
    trackCollectionViewed();
    trackCartPage();
    bindCartDrawerOpen();
    interceptAddToCart();
    bindCheckoutClicks();
  }

  // PostHog loader is async — wait briefly, but still register listeners so
  // events fire as soon as posthog becomes available (queue handles offline).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForPosthog(5000).then(init);
    });
  } else {
    waitForPosthog(5000).then(init);
  }
})();

/* Append once to assets/posthog-events.js. No new SDK or capture call. */
;(() => {
  'use strict';
  if (window.__kpscConversionBridgeInstalled) return;
  window.__kpscConversionBridgeInstalled = true;
  const BRIDGE = 'kpsc_ph_conversion_v1';
  const ATTRIBUTION = 'kpsc_oppref_v1';
  const DOMAIN = 'kpopseoulcity.com';
  if (!['kpopseoulcity.com', 'www.kpopseoulcity.com'].includes(location.hostname)) return;
  const validId = value => typeof value === 'string' &&
    (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) || /^shopify_customer_[0-9]+$/.test(value));
  const validRef = value => typeof value === 'string' && value.length > 0 && value.length <= 1200 && !/[\s\x00-\x1f]/.test(value);
  const set = (name, value, age) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Domain=${DOMAIN}; Max-Age=${age}; SameSite=Lax; Secure`;
  };
  const read = name => {
    try {
      const item = document.cookie.split('; ').find(part => part.startsWith(`${name}=`));
      return item ? JSON.parse(decodeURIComponent(item.slice(name.length + 1))) : null;
    } catch (_) { return null; }
  };
  const clear = () => { set(BRIDGE, '', 0); set(ATTRIBUTION, '', 0); };
  const allowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed() === true &&
        privacy.marketingAllowed() === true && privacy.saleOfDataAllowed() === true;
    } catch (_) { return false; }
  };
  let lastWrite = 0;
  let lastIdentity = '';
  let privacyLoading = false;
  function update(force = false) {
    const shopify = window.Shopify;
    if (!shopify?.customerPrivacy && shopify?.loadFeatures && !privacyLoading) {
      privacyLoading = true;
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
        privacyLoading = false;
        if (!error) update(true);
      });
    }
    if (!allowed()) { clear(); lastWrite = 0; return; }
    const ph = window.posthog;
    // Queue stubs contain these method names too: never call them before initialization.
    if (!ph || ph.__loaded !== true || typeof ph.has_opted_out_capturing !== 'function' ||
        typeof ph.get_distinct_id !== 'function' || typeof ph.get_session_id !== 'function') {
      set(BRIDGE, '', 0); return;
    }
    try {
      if (ph.has_opted_out_capturing() !== false) { clear(); return; }
      const distinctId = ph.get_distinct_id();
      const sessionId = ph.get_session_id();
      if (!validId(distinctId) || !validId(sessionId)) { set(BRIDGE, '', 0); return; }
      const now = Date.now();
      const previous = read(ATTRIBUTION);
      // Read the genuine URL parameter only after permission; never synthesize it.
      const landingRef = new URL(location.href).searchParams.get('oppref');
      let attribution = previous && validRef(previous.value) && now - previous.at < 30 * 86400000 && now >= previous.at ? previous : null;
      if (validRef(landingRef) && landingRef !== attribution?.value) {
        attribution = { value: landingRef, at: now };
        set(ATTRIBUTION, JSON.stringify(attribution), 30 * 86400);
      }
      const identity = JSON.stringify([distinctId, sessionId, attribution?.value]);
      if (!force && identity === lastIdentity && now - lastWrite < 10000) return;
      set(BRIDGE, JSON.stringify({ v: 1, at: now, distinct_id: distinctId,
        session_id: sessionId, oppref: attribution?.value || null,
        analytics: true, marketing: true, sale_of_data: true }), 86400);
      lastWrite = now;
      lastIdentity = identity;
    } catch (_) { set(BRIDGE, '', 0); }
  }
  document.addEventListener('visitorConsentCollected', () => update(true));
  document.addEventListener('click', () => update(true), true);
  document.addEventListener('submit', () => update(true), true);
  window.addEventListener('pageshow', () => update(true));
  // Handles an SDK loaded asynchronously after this existing theme asset.
  setInterval(() => update(), 1000);
  update(true);
})();
